/**
 * Phase 2 — on-page SEO + structured data helpers.
 *
 * Every value emitted here is derived from existing production data
 * (catalog products, collections, categories). Nothing is invented: when a
 * field is missing on the record it is simply omitted from the output.
 *
 * Built on top of the Phase 1 foundation in `./site.ts` — no duplicate
 * canonical/URL logic lives here.
 */
import { SITE_NAME, SITE_URL, absoluteImage, canonicalUrl } from "./site";
import type { CatalogProduct } from "@/lib/catalog/products";
import { BOTTLE_SIZE_IDS, sizePrices } from "@/lib/catalog/size-pricing";
import { getDiscountedPrice } from "@/lib/cart/pricing";

/** Brand name used in titles and Product/Brand schema. */
export const BRAND_NAME = "CB16";

type JsonLd = Record<string, unknown>;

/** Trim + collapse whitespace, then clamp to a search-friendly length. */
export function clampDescription(input: string, max = 160): string {
  const text = input.replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\-—\s]+$/, "")}…`;
}

/**
 * SEO title for a product page. Reuses an existing `metaTitle` when the
 * client has authored one; otherwise "[Product Name] | CB16 Fragrances".
 */
export function productSeoTitle(product: CatalogProduct): string {
  const existing = product.metaTitle?.trim();
  if (existing) return existing;
  return `${product.name} | ${BRAND_NAME} Fragrances`;
}

/**
 * SEO description for a product page, assembled only from stored product
 * information: authored meta description → short description → long
 * description → a factual sentence from name / family / concentration / notes.
 */
export function productSeoDescription(product: CatalogProduct): string {
  const authored = product.metaDescription?.trim() || product.shortDescription?.trim();
  if (authored) return clampDescription(authored);
  const long = product.longDescription?.trim();
  if (long) return clampDescription(long);

  const facts: string[] = [];
  if (product.concentration) facts.push(product.concentration);
  if (product.family) facts.push(`${product.family} fragrance`);
  if (product.gender) facts.push(`for ${product.gender.toLowerCase()}`);
  const lead = facts.length ? `${product.name} — ${facts.join(" ")}.` : `${product.name}.`;
  const notes = product.notes?.trim() ? ` Notes: ${product.notes.trim()}.` : "";
  return clampDescription(`${lead}${notes} Available at ${SITE_NAME}.`);
}

/** Meaningful, non-stuffed alt text for a product image. */
export function productImageAlt(name: string, index = 0): string {
  const base = `${name} perfume by ${BRAND_NAME}`;
  return index > 0 ? `${base} — view ${index + 1}` : base;
}

/** Absolute, de-duplicated image URLs for structured data. */
export function productSchemaImages(product: CatalogProduct): string[] {
  const raw = [product.img, ...(product.gallery ?? [])];
  const out: string[] = [];
  for (const src of raw) {
    const abs = absoluteImage(src);
    if (abs && !out.includes(abs)) out.push(abs);
  }
  return out.slice(0, 5);
}

/**
 * Product structured data built strictly from stored values.
 *
 * Existing per-size prices (10 / 20 / 50 / 100 ML) become one Offer each,
 * mirroring the storefront's own variant + discount computation. No stock
 * levels are invented: availability follows the product status only.
 */
export function productSchema(product: CatalogProduct): JsonLd {
  const url = canonicalUrl(`/fragrances/${product.slug}`);
  const images = productSchemaImages(product);
  const prices = sizePrices(product);
  const availability =
    product.status === "archived"
      ? "https://schema.org/Discontinued"
      : product.status === "coming-soon"
        ? "https://schema.org/PreOrder"
        : "https://schema.org/InStock";

  const offers = BOTTLE_SIZE_IDS.filter((id) => (prices[id] ?? 0) > 0).map((id) => ({
    "@type": "Offer",
    name: `${product.name} — ${id} ML`,
    price: getDiscountedPrice(prices[id], product.discountPercent).toFixed(2),
    priceCurrency: "INR",
    availability,
    itemCondition: "https://schema.org/NewCondition",
    url,
    ...(product.sku ? { sku: `${product.sku}-${id}ML` } : {}),
  }));

  const schema: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: productSeoDescription(product),
    url,
    brand: { "@type": "Brand", name: BRAND_NAME },
    ...(images.length ? { image: images } : {}),
    ...(product.sku ? { sku: product.sku } : {}),
    ...(product.family ? { category: `Fragrance / ${product.family}` } : {}),
    ...(product.gender ? { audience: { "@type": "PeopleAudience", suggestedGender: product.gender } } : {}),
  };

  if (offers.length > 1) {
    const values = offers.map((o) => Number(o.price));
    schema["offers"] = {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: Math.min(...values).toFixed(2),
      highPrice: Math.max(...values).toFixed(2),
      offerCount: offers.length,
      availability,
      url,
      offers,
    };
  } else if (offers.length === 1) {
    schema["offers"] = offers[0];
  }

  return schema;
}

export type Crumb = { name: string; path?: string };

/** BreadcrumbList structured data from the site's real navigation hierarchy. */
export function breadcrumbSchema(crumbs: Crumb[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      ...(c.path ? { item: canonicalUrl(c.path) } : {}),
    })),
  };
}

/**
 * Organization schema — only fields the application already knows.
 * Address, phone and social profiles are intentionally omitted because they
 * are not stored anywhere in the app.
 */
export function organizationSchema(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    alternateName: BRAND_NAME,
    url: `${SITE_URL}/`,
    logo: absoluteImage("/favicon.svg"),
  };
}

/** WebSite schema with the existing on-site search endpoint. */
export function webSiteSchema(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: "en-IN",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** ItemList structured data for a listing page (shop / collection / category). */
export function itemListSchema(products: { name: string; slug: string }[], limit = 24): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    numberOfItems: products.length,
    itemListElement: products.slice(0, limit).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.name,
      url: canonicalUrl(`/fragrances/${p.slug}`),
    })),
  };
}

/** Convenience: a JSON-LD `scripts` entry for route `head()`. */
export function ldScript(schema: JsonLd) {
  return { type: "application/ld+json", children: JSON.stringify(schema) } as const;
}
